import {
  Actions,
  URVersion,
  V4Planner,
} from "@uniswap/v4-sdk";
import {
  CommandType,
  RoutePlanner,
  UniversalRouterVersion,
} from "@uniswap/universal-router-sdk";
import {
  encodeAbiParameters,
  getAddress,
  isAddress,
  keccak256,
  parseEther,
  zeroAddress,
  type Address,
  type Hex,
} from "viem";

import { publicClient, targetChain } from "@stonkland/protocol/chain";
import { addresses } from "@stonkland/protocol/contracts";
import {
  publicEnvironment,
  type PublicEnvironmentName,
} from "@stonkland/protocol/public-env";

export const LAND_POOL_FEE = 10_000;
export const LAND_POOL_TICK_SPACING = 100;
export const LAND_SWAP_GAS_RESERVE = parseEther("0.001");
export const LAND_SWAP_SLIPPAGE_OPTIONS = [50, 100, 200] as const;

const MAX_UINT128 = (1n << 128n) - 1n;

const requiredAddress = (
  name: PublicEnvironmentName,
  options: { allowZero?: boolean } = {},
) => {
  const value = publicEnvironment(name)?.trim();
  if (!value || !isAddress(value)) {
    throw new Error(`${name} must be a deployed address.`);
  }
  const address = getAddress(value);
  if (!options.allowZero && address === zeroAddress) {
    throw new Error(`${name} cannot be the zero address.`);
  }
  return address;
};

const requiredBytes32 = (name: PublicEnvironmentName) => {
  const value = publicEnvironment(name)?.trim();
  if (!value || !/^0x[0-9a-fA-F]{64}$/.test(value)) {
    throw new Error(`${name} must be a bytes32 value.`);
  }
  return value.toLowerCase() as Hex;
};

export type LandPoolRuntime = {
  hooks: Address;
  permit2: Address;
  poolId: Hex;
  poolKey: LandPoolKey;
  poolManager: Address;
  quoter: Address;
  stateView: Address;
  universalRouter: Address;
};

type LandPoolKey = {
  currency0: Address;
  currency1: Address;
  fee: number;
  tickSpacing: number;
  hooks: Address;
};

export function requireLandPoolRuntime(): LandPoolRuntime {
  if (targetChain.id !== 4_663 && targetChain.id !== 46_630) {
    throw new Error("LAND trading requires Robinhood Chain.");
  }
  const land = addresses.land;
  if (!land) throw new Error("LAND contract is not configured.");

  const tickSpacing = Number(
    publicEnvironment("VITE_UNISWAP_V4_TICK_SPACING")?.trim(),
  );
  if (tickSpacing !== LAND_POOL_TICK_SPACING) {
    throw new Error(
      `VITE_UNISWAP_V4_TICK_SPACING must be ${LAND_POOL_TICK_SPACING}.`,
    );
  }
  const hooks = requiredAddress("VITE_UNISWAP_V4_HOOKS_ADDRESS", {
    allowZero: true,
  });
  if (hooks !== zeroAddress) {
    throw new Error("The LAND launch pool must be hookless.");
  }

  const poolKey: LandPoolKey = {
    currency0: zeroAddress,
    currency1: land,
    fee: LAND_POOL_FEE,
    tickSpacing,
    hooks,
  };
  const computedPoolId = keccak256(
    encodeAbiParameters(
      [
        { type: "address" },
        { type: "address" },
        { type: "uint24" },
        { type: "int24" },
        { type: "address" },
      ],
      [zeroAddress, land, LAND_POOL_FEE, tickSpacing, hooks],
    ),
  );
  const poolId = requiredBytes32("VITE_UNISWAP_V4_POOL_ID");
  if (poolId !== computedPoolId.toLowerCase()) {
    throw new Error("VITE_UNISWAP_V4_POOL_ID does not match the LAND pool key.");
  }

  return {
    hooks,
    permit2: requiredAddress("VITE_UNISWAP_PERMIT2_ADDRESS"),
    poolId,
    poolKey,
    poolManager: requiredAddress("VITE_UNISWAP_V4_POOL_MANAGER_ADDRESS"),
    quoter: requiredAddress("VITE_UNISWAP_V4_QUOTER_ADDRESS"),
    stateView: requiredAddress("VITE_UNISWAP_V4_STATE_VIEW_ADDRESS"),
    universalRouter: requiredAddress(
      "VITE_UNISWAP_V4_UNIVERSAL_ROUTER_ADDRESS",
    ),
  };
}

export const uniswapV4QuoterAbi = [
  {
    type: "function",
    name: "quoteExactInputSingle",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          {
            name: "poolKey",
            type: "tuple",
            components: [
              { name: "currency0", type: "address" },
              { name: "currency1", type: "address" },
              { name: "fee", type: "uint24" },
              { name: "tickSpacing", type: "int24" },
              { name: "hooks", type: "address" },
            ],
          },
          { name: "zeroForOne", type: "bool" },
          { name: "exactAmount", type: "uint128" },
          { name: "hookData", type: "bytes" },
        ],
      },
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "gasEstimate", type: "uint256" },
    ],
  },
] as const;

export const universalRouterAbi = [
  {
    type: "function",
    name: "execute",
    stateMutability: "payable",
    inputs: [
      { name: "commands", type: "bytes" },
      { name: "inputs", type: "bytes[]" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

export const permit2Abi = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "user", type: "address" },
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
      { name: "nonce", type: "uint48" },
    ],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
    ],
    outputs: [],
  },
] as const;

export type LandSwapQuote = {
  amountIn: bigint;
  amountOut: bigint;
  gasEstimate: bigint;
  minimumAmountOut: bigint;
  quotedAt: number;
  slippageBps: number;
  zeroForOne: boolean;
};

export function parseEthAmount(value: string) {
  const normalized = value.trim();
  if (!/^(?:\d+|\d+\.\d{1,18}|\.\d{1,18})$/.test(normalized)) {
    return undefined;
  }
  try {
    return parseEther(normalized.startsWith(".") ? `0${normalized}` : normalized);
  } catch {
    return undefined;
  }
}

export const parseLandAmount = parseEthAmount;

export function minimumLandForSlippage(
  amountOut: bigint,
  slippageBps: number,
) {
  if (
    !Number.isInteger(slippageBps) ||
    slippageBps < 0 ||
    slippageBps >= 10_000
  ) {
    throw new Error("LAND swap slippage must be from 0 through 9,999 bps.");
  }
  return (amountOut * BigInt(10_000 - slippageBps)) / 10_000n;
}

async function quoteExactInput(
  amountIn: bigint,
  slippageBps: number,
  zeroForOne: boolean,
): Promise<LandSwapQuote> {
  if (amountIn <= 0n) throw new Error("Enter an amount greater than zero.");
  if (amountIn > MAX_UINT128) throw new Error("LAND swap amount is too large.");
  const runtime = requireLandPoolRuntime();
  const { result } = await publicClient.simulateContract({
    address: runtime.quoter,
    abi: uniswapV4QuoterAbi,
    functionName: "quoteExactInputSingle",
    args: [
      {
        poolKey: runtime.poolKey,
        zeroForOne,
        exactAmount: amountIn,
        hookData: "0x",
      },
    ],
  });
  const [amountOut, gasEstimate] = result;
  if (amountOut <= 0n) throw new Error("Uniswap v4 returned no output.");
  return {
    amountIn,
    amountOut,
    gasEstimate,
    minimumAmountOut: minimumLandForSlippage(amountOut, slippageBps),
    quotedAt: Date.now(),
    slippageBps,
    zeroForOne,
  };
}

export function quoteLandToEth(amountIn: bigint, slippageBps: number) {
  return quoteExactInput(amountIn, slippageBps, false);
}

export function quoteEthToLand(amountIn: bigint, slippageBps: number) {
  return quoteExactInput(amountIn, slippageBps, true);
}

export function buildLandSwapPlan(quote: LandSwapQuote) {
  const runtime = requireLandPoolRuntime();
  const inputCurrency = quote.zeroForOne ? zeroAddress : runtime.poolKey.currency1;
  const outputCurrency = quote.zeroForOne ? runtime.poolKey.currency1 : zeroAddress;
  const v4Planner = new V4Planner();
  v4Planner.addAction(
    Actions.SWAP_EXACT_IN_SINGLE,
    [
      {
        poolKey: runtime.poolKey,
        zeroForOne: quote.zeroForOne,
        amountIn: quote.amountIn.toString(),
        amountOutMinimum: quote.minimumAmountOut.toString(),
        minHopPriceX36: "0",
        hookData: "0x",
      },
    ],
    URVersion.V2_1_1,
  );
  v4Planner.addAction(
    Actions.SETTLE_ALL,
    [inputCurrency, quote.amountIn.toString()],
    URVersion.V2_1_1,
  );
  v4Planner.addAction(
    Actions.TAKE_ALL,
    [outputCurrency, quote.minimumAmountOut.toString()],
    URVersion.V2_1_1,
  );

  const route = new RoutePlanner();
  route.addCommand(
    CommandType.V4_SWAP,
    [v4Planner.finalize()],
    false,
    UniversalRouterVersion.V2_1_1,
  );
  return {
    commands: route.commands as Hex,
    inputs: route.inputs as Hex[],
    runtime,
  };
}
